/**
 * WindowSpec — AI Window Composer DSL (Faz 9 S2)
 *
 * Forward-compat kurallari:
 *   - version field YOK; eski WindowSpec'ler sonsuza kadar calisir
 *   - Yeni prop'lar her zaman optional eklenir
 *   - Mevcut prop'lar hicbir zaman silinemez, kaldirilamaz
 *   - type/children semantigi degistirilemez (breaking change yasak)
 *
 * Referans: docs/desktop/window-spec.md
 */
import { z } from 'zod';
/**
 * Declarative dataSource.filter — Faz 10 dilim 3
 *
 * Client-side post-fetch suzmesi: dataSource tool sonucu array'i field/op/value
 * ile filtreler. LLM "set dataSource.filter {...}" ile atar.
 *
 * - field: Nokta-ayiracli alan yolu (orn: 'role', 'profile.role')
 * - op: eq | ne | in | nin | contains
 * - value: scalar (eq/ne/contains) veya array (in/nin)
 */
const dataSourceFilterSchema = z.object({
    /** Filtrelenecek alan yolu — nokta-ayiracli desteklenir (orn: 'role', 'profile.role') */
    field: z.string().min(1),
    /**
     * Karsilastirma operatoru:
     * - eq: esit
     * - ne: esit degil
     * - in: value array'i icinde
     * - nin: value array'i disinda
     * - contains: string iceriyor (substring)
     */
    op: z.enum(['eq', 'ne', 'in', 'nin', 'contains']),
    /**
     * Karsilastirma degeri.
     * in/nin icin array; eq/ne/contains icin scalar.
     */
    value: z.unknown(),
});
/**
 * Declarative dataSource — Faz 10
 *
 * Node'un child'larini dinamik veri kaynagindan (AI tool) doldurur.
 * Runtime'da useDataSource hook tarafindan islenir.
 *
 * - tool: Kayitli AI tool adi (orn: 'platform.list_users')
 * - args: Tool parametreleri (static)
 * - mapTo: Sonucun hangi alana map'lenecegi
 *   - 'children': (default, runtime destekli) sonuclari node.children olarak inject eder
 *   - 'rows': (runtime destekli) list/Table besleme yolu — props.rows'a satir dizisi baglar
 *   - 'record': (runtime destekli, G4 gh#1571) TEK-kayit detail yolu — get sonucu tek kaydi
 *     mevcut children'in {{field}} placeholder'larina baglar (children REPLACE edilmez)
 *   - 'items': forward-compat; Zod'da tanimli ama renderer TODO
 * - itemTemplate: Her item icin WindowSpec sablonu; {{field}} interpolasyonu desteklenir
 *   (text-only, XSS-safe — HTML/innerHTML YASAK)
 * - filter: (Faz 10 dilim 3) Client-side post-fetch suzmesi — LLM "set dataSource.filter" ile atar
 */
const dataSourceSchema = z.object({
    /** Kayitli AI tool adi */
    tool: z.string(),
    /** Tool parametreleri (static, opsiyonel) */
    args: z.record(z.unknown()).optional(),
    /**
     * Sonucun hangi alana map'lenecegi.
     * Runtime: 'children' + 'rows' + 'record' destekleniyor.
     * 'items': Zod'da tanimli, forward-compat icin.
     */
    mapTo: z.enum(['children', 'items', 'rows', 'record']).default('children'),
    /**
     * Her item icin WindowSpec sablonu.
     * {{field.path}} syntax ile interpolasyon desteklenir (text-only, XSS-safe).
     * Orn: { type: 'Row', props: { primary: '{{displayName}}', secondary: '{{email}}' } }
     */
    itemTemplate: z.unknown().optional(),
    /**
     * Client-side post-fetch suzmesi — Faz 10 dilim 3.
     * Fetch tamamlandiktan sonra items array'i bu filter ile suzulur.
     * LLM: set dataSource.filter { field: 'role', op: 'ne', value: 'admin' }
     */
    filter: dataSourceFilterSchema.optional(),
});
// z.lazy ile recursive tanimlama icin once schema referansi olustur
const windowSpecSchema = z.lazy(() => z.object({
    /**
     * @elmes/primitives registry'sindeki primitif adi.
     * Her zaman PascalCase olmali — "stack", "STACK" gecersiz.
     */
    type: z.string(),
    /**
     * Faz 10 mutate_window icin anchor noktasi.
     * Runtime'da data-compose-id olarak DOM'a yansitilir.
     */
    id: z.string().optional(),
    /**
     * Primitife ozgu prop'lar.
     * Her primitif kendi Zod schema'siyla runtime'da validate eder.
     * Burada record<unknown> — tip guvenligi renderer katmaninda.
     */
    props: z.record(z.unknown()).optional(),
    /**
     * Cocuk node'lar — recursive WindowSpec dizisi.
     * Primitif children desteklemiyorsa renderer tarafindan gormezden gelinir.
     */
    children: z.array(z.lazy(() => windowSpecSchema)).optional(),
    /**
     * Declarative dataSource — Faz 10.
     * Mevcutsa useDataSource hook tarafindan islenerek children'a inject edilir.
     * LLM bu alani kullanabilir — 'DO NOT inline' users/data.
     */
    dataSource: dataSourceSchema.optional(),
}));
export { windowSpecSchema, dataSourceSchema, dataSourceFilterSchema };
// ─── Board node types (Faz 6 PR-3) ──────────────────────────────────────────
//
// Additive, deklaratif Board (kanban) node type'lari. Mevcut generic WindowSpec
// semasi (type: string) DEGISMEZ — Board/BoardColumn birer node type stringidir.
// Card node'u mevcut @elmes/primitives 'Card' primitifini yeniden kullanir.
//
// Forward-compat: hicbir mevcut prop/type semantigi degismez; yalnizca yeni
// opsiyonel semalar + saf dagitim yardimcisi eklenir. DSL'e native event/action
// EKLENMEZ — etkilesim (drag-drop -> tool call) PR-4'te dis stateful baglayicida.
/**
 * Board kolon tanimi — Board.props.columns[] icindeki her ogenin sekli.
 * - id: Kolon kimligi; item[groupBy] degeriyle esleserek dagitimda kullanilir.
 * - label: Kolon basligi (gorsel).
 */
export const boardColumnDefSchema = z.object({
    id: z.string().min(1),
    label: z.string(),
});
/**
 * Board node prop semasi (type: 'Board').
 * - groupBy: dataSource item'larinin hangi alanina gore kolonlara dagitilacagi
 *   (nokta-ayiracli path desteklenir, orn: 'status' veya 'meta.state').
 * - columns: kolon tanimlari; sira gorsel siralamadir.
 */
export const boardPropsSchema = z.object({
    groupBy: z.string().min(1),
    columns: z.array(boardColumnDefSchema).min(1),
});
/**
 * BoardColumn node prop semasi (type: 'BoardColumn').
 * useDataSource / statik author bu node'lari Board.children olarak uretir;
 * her BoardColumn'un children'i Card node'lardir.
 */
export const boardColumnPropsSchema = z.object({
    columnId: z.string().min(1),
    label: z.string(),
});
/** Board ekosistemine ait node type isimleri. */
export const BOARD_NODE_TYPES = ['Board', 'BoardColumn', 'Card'];
/** type stringi Board ekosistemine ait bir node type mi? */
export function isBoardNodeType(type) {
    return BOARD_NODE_TYPES.includes(type);
}
/**
 * Nokta-ayiracli alan yolu ile obje icinden deger okur (useDataSource.getNested muadili;
 * shared'da React'siz saf tutulur, dagitim yardimcisinin bagimsiz test edilebilmesi icin).
 */
function readField(obj, path) {
    if (obj === null || obj === undefined)
        return undefined;
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
        if (current === null || current === undefined || typeof current !== 'object') {
            return undefined;
        }
        current = current[key];
    }
    return current;
}
/**
 * Saf dagitim yardimcisi — dataSource tool sonucu item'larini `groupBy` degerine
 * gore kolonlara dagitir. Deterministik ve React'siz (bagimsiz unit-test edilebilir).
 *
 * Kurallar:
 * - Her item, item[groupBy] === column.id olan kolona konur (String() ile normalize).
 * - Bir item birden fazla kolona ASLA gitmez (ilk eslesen kolona — id'ler uniq varsayilir).
 * - Hicbir kolona uymayan item drop edilir (deterministik; kayitsiz kolon uydurulmaz).
 * - Kolon sirasi girdi `columns` sirasidir; her kolonda item sirasi girdi sirasidir.
 *
 * @param items    dataSource'tan gelen ham kayitlar
 * @param groupBy  gruplama alani yolu (nokta-ayiracli)
 * @param columns  hedef kolon tanimlari
 */
export function distributeItemsToColumns(items, groupBy, columns) {
    const buckets = columns.map((column) => ({ column, items: [] }));
    const byId = new Map();
    for (const bucket of buckets)
        byId.set(bucket.column.id, bucket);
    for (const item of items) {
        const raw = readField(item, groupBy);
        if (raw === null || raw === undefined)
            continue;
        const key = String(raw);
        const bucket = byId.get(key);
        if (bucket)
            bucket.items.push(item);
    }
    return buckets;
}
//# sourceMappingURL=windowSpec.js.map